import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { createFloc, getFlocs } from '../../services/flocService';
import { FlocType, Criticality, FunctionalLocation } from '@easydone/shared';
import { FlocTreeSelect } from '../../components/FlocTreeSelect';

export function NewFlocPage() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [parentFlocId, setParentFlocId] = useState<string>('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<FlocType>('equipment_position');
  const [criticality, setCriticality] = useState<Criticality>('medium');
  const [functionalSystem, setFunctionalSystem] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadFlocs();
  }, [currentOrg]);

  const loadFlocs = async () => {
    if (!currentOrg) return;
    try {
      const flocsData = await getFlocs(currentOrg.id);
      setFlocs(flocsData);
    } catch (err) {
      console.error('Failed to load FLOCs:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrg || !currentUser) return;

    setCreating(true);
    setError('');

    try {
      // Build create data, omitting undefined values
      const createData: any = {
        code,
        name,
        type,
        criticality,
      };
      if (description) createData.description = description;
      if (functionalSystem) createData.functionalSystem = functionalSystem;
      if (parentFlocId) createData.parentFlocId = parentFlocId;

      const floc = await createFloc(currentOrg.id, createData, currentUser.uid);

      navigate(`/flocs/${floc.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create functional location');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Create Functional Location</h1>
        <p className="text-gray-600 mt-1">
          Add a new position in your facility hierarchy
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Parent Location (Optional)
          </label>
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setParentFlocId('')}
              className={`btn text-sm ${
                parentFlocId === '' ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              None (Top Level)
            </button>
          </div>
          {flocs.length > 0 && (
            <>
              <p className="text-xs text-gray-600 mb-2">
                Or select a parent from the hierarchy:
              </p>
              <FlocTreeSelect
                flocs={flocs}
                selectedId={parentFlocId}
                onSelect={(id) => setParentFlocId(id)}
              />
            </>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Create this FLOC under an existing location to build your facility hierarchy
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            FLOC Code *
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g., SITE-A/BLDG-1/PUMP-01"
            className="input w-full"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Hierarchical code identifying this location
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Main Cooling Pump"
            className="input w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details about this location..."
            className="input w-full"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type *
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as FlocType)}
            className="input w-full"
            required
          >
            <option value="site">Site</option>
            <option value="building">Building</option>
            <option value="floor">Floor</option>
            <option value="area">Area</option>
            <option value="zone">Zone</option>
            <option value="system">System</option>
            <option value="equipment_position">Equipment Position</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Functional System
          </label>
          <input
            type="text"
            value={functionalSystem}
            onChange={(e) => setFunctionalSystem(e.target.value)}
            placeholder="e.g., HVAC, Electrical, Water Treatment"
            className="input w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Criticality *
          </label>
          <select
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as Criticality)}
            className="input w-full"
            required
          >
            <option value="not_in_use">Not in use - Asset not installed or operational</option>
            <option value="very_low">Very low - Minimal impact if unavailable</option>
            <option value="low">Low - Minor impact if unavailable</option>
            <option value="medium">Medium - Moderate impact</option>
            <option value="high">High - Significant impact</option>
            <option value="very_high">Very high - Operations cannot continue</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            How important is this location to operations?
          </p>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/flocs')}
            className="btn btn-secondary"
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={creating}
          >
            {creating ? 'Creating...' : 'Create FLOC'}
          </button>
        </div>
      </form>
    </div>
  );
}
